// client/src/components/TeamSelection.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Card from './Card';
import { fetchSelectionData, setUserTeam } from '../services/api';

const TeamSelection = ({ userId, onTeamConfirmed }) => {
  const [goldCards, setGoldCards] = useState([]);
  const [silverCards, setSilverCards] = useState([]);
  const [bronzeCards, setBronzeCards] = useState([]);
  const [fixedCards, setFixedCards] = useState([]);
  const [selectedGold, setSelectedGold] = useState(null);
  const [selectedSilver, setSelectedSilver] = useState(null);
  const [selectedBronze, setSelectedBronze] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [randomizing, setRandomizing] = useState(false);

  useEffect(() => {
    const loadCards = async () => {
      try {
        const res = await fetchSelectionData();
        setGoldCards(res.data.gold || []);
        setSilverCards(res.data.silver || []);
        setBronzeCards(res.data.bronze || []);
        setFixedCards(res.data.fixed || []);
      } catch (err) {
        console.error(err);
        setError('Failed to load cards. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    };
    loadCards();
  }, []);

  const getGoalkeeperCount = () => {
    const selected = [selectedGold, selectedSilver, selectedBronze].filter(c => c !== null);
    return selected.filter(c => c.position === 'Goalkeeper').length;
  };

  const isGoalkeeperRuleValid = () => {
    const count = getGoalkeeperCount();
    return count >= 1 && count <= 2;
  };

  // Random selection that guarantees exactly ONE goalkeeper
  const generateRandomSelection = () => {
    if (goldCards.length === 0 || silverCards.length === 0 || bronzeCards.length === 0) return;

    setRandomizing(true);

    const randomItem = (arr) => arr[Math.floor(Math.random() * arr.length)];

    const goldGk = goldCards.filter(c => c.position === 'Goalkeeper');
    const goldOut = goldCards.filter(c => c.position !== 'Goalkeeper');
    const silverGk = silverCards.filter(c => c.position === 'Goalkeeper');
    const silverOut = silverCards.filter(c => c.position !== 'Goalkeeper');
    const bronzeGk = bronzeCards.filter(c => c.position === 'Goalkeeper');
    const bronzeOut = bronzeCards.filter(c => c.position !== 'Goalkeeper');

    const availableGkTiers = [];
    if (goldGk.length > 0) availableGkTiers.push('gold');
    if (silverGk.length > 0) availableGkTiers.push('silver');
    if (bronzeGk.length > 0) availableGkTiers.push('bronze');

    if (availableGkTiers.length === 0) {
      setSelectedGold(randomItem(goldCards));
      setSelectedSilver(randomItem(silverCards));
      setSelectedBronze(randomItem(bronzeCards));
      setRandomizing(false);
      return;
    }

    const gkTier = randomItem(availableGkTiers);
    let newGold, newSilver, newBronze;

    if (gkTier === 'gold') {
      newGold = randomItem(goldGk);
      newSilver = randomItem(silverOut.length ? silverOut : silverCards);
      newBronze = randomItem(bronzeOut.length ? bronzeOut : bronzeCards);
    } else if (gkTier === 'silver') {
      newGold = randomItem(goldOut.length ? goldOut : goldCards);
      newSilver = randomItem(silverGk);
      newBronze = randomItem(bronzeOut.length ? bronzeOut : bronzeCards);
    } else {
      newGold = randomItem(goldOut.length ? goldOut : goldCards);
      newSilver = randomItem(silverOut.length ? silverOut : silverCards);
      newBronze = randomItem(bronzeGk);
    }

    setSelectedGold(newGold);
    setSelectedSilver(newSilver);
    setSelectedBronze(newBronze);
    setRandomizing(false);
  };

  const confirmTeam = async () => {
    if (!selectedGold || !selectedSilver || !selectedBronze) {
      alert('Please choose one Gold, one Silver, and one Bronze card.');
      return;
    }
    if (!isGoalkeeperRuleValid()) {
      const count = getGoalkeeperCount();
      if (count === 0) {
        alert('You must select at least one Goalkeeper from any tier (Gold, Silver, or Bronze).');
      } else if (count > 2) {
        alert('You cannot select three Goalkeepers. Maximum two.');
      }
      return;
    }
    const selectedIds = [selectedGold.id, selectedSilver.id, selectedBronze.id];
    try {
      await setUserTeam(userId, selectedIds);
      onTeamConfirmed();
    } catch (err) {
      console.error(err);
      alert('Error saving team: ' + (err.response?.data?.error || err.message));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-900 to-black flex items-center justify-center">
        <div className="text-white text-2xl animate-pulse">Loading selection cards...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-900 to-black flex items-center justify-center">
        <div className="text-red-500 text-xl">{error}</div>
      </div>
    );
  }

  const selectionComplete = selectedGold && selectedSilver && selectedBronze && isGoalkeeperRuleValid();
  const gkCount = getGoalkeeperCount();

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-900 to-black p-4 sm:p-6 pb-32 text-white">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl sm:text-4xl font-bold text-center text-yellow-400 mb-2">
          ⚽ Build Your Squad
        </h1>
        <p className="text-center text-gray-300 mb-8 text-sm sm:text-base">
          Choose <span className="text-yellow-300 font-bold">1 Gold</span>,{' '}
          <span className="text-gray-300 font-bold">1 Silver</span>, and{' '}
          <span className="text-amber-500 font-bold">1 Bronze</span> card.
          <br />
          <span className="text-yellow-300">⚠️ Must have at least one Goalkeeper (max two).</span>
        </p>

        {/* Randomize Button */}
        <div className="flex justify-center mb-10">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={generateRandomSelection}
            disabled={randomizing || goldCards.length === 0}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-8 rounded-full shadow-lg transition flex items-center gap-2 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {randomizing ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Randomizing...
              </>
            ) : (
              <>
                🎲 Random Selection
                <span className="text-xs text-indigo-200 ml-1">(exactly 1 goalkeeper)</span>
              </>
            )}
          </motion.button>
        </div>

        {/* Gold Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-yellow-400 mb-4 flex items-center gap-2">
            <span>💰</span> Gold Cards <span className="text-sm text-gray-400 font-normal">(pick exactly one)</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 justify-items-center">
            {goldCards.map(card => (
              <div
                key={card.id}
                onClick={() => setSelectedGold(card)}
                className="cursor-pointer w-full max-w-[240px] transition-transform hover:scale-105"
              >
                <Card
                  player={card}
                  isSelected={selectedGold?.id === card.id}
                  onSelect={() => setSelectedGold(card)}
                  disabled={false}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Silver Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-300 mb-4 flex items-center gap-2">
            <span>🔘</span> Silver Cards <span className="text-sm text-gray-400 font-normal">(pick exactly one)</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 justify-items-center">
            {silverCards.map(card => (
              <div
                key={card.id}
                onClick={() => setSelectedSilver(card)}
                className="cursor-pointer w-full max-w-[240px] transition-transform hover:scale-105"
              >
                <Card
                  player={card}
                  isSelected={selectedSilver?.id === card.id}
                  onSelect={() => setSelectedSilver(card)}
                  disabled={false}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Bronze Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-amber-500 mb-4 flex items-center gap-2">
            <span>🟤</span> Bronze Cards <span className="text-sm text-gray-400 font-normal">(pick exactly one)</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 justify-items-center">
            {bronzeCards.map(card => (
              <div
                key={card.id}
                onClick={() => setSelectedBronze(card)}
                className="cursor-pointer w-full max-w-[240px] transition-transform hover:scale-105"
              >
                <Card
                  player={card}
                  isSelected={selectedBronze?.id === card.id}
                  onSelect={() => setSelectedBronze(card)}
                  disabled={false}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Fixed Cards Preview - now expects 7 cards from API */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-blue-400 mb-4 flex items-center gap-2">
            <span>🛡️</span> Fixed Common Cards <span className="text-sm text-gray-400 font-normal">(auto‑included)</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 justify-items-center">
            {fixedCards.map(card => (
              <div key={card.id} className="w-full max-w-[240px] opacity-80">
                <Card player={card} isSelected={false} onSelect={() => {}} disabled={true} />
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-400 text-center mt-3">
            These 7 common cards are automatically added to every player’s team for balance.
          </p>
        </div>

        {/* Goalkeeper warning / status */}
        {selectedGold && selectedSilver && selectedBronze && (
          <div className={`text-center mb-4 text-sm font-semibold ${gkCount === 0 ? 'text-red-400' : gkCount > 2 ? 'text-red-400' : 'text-green-400'}`}>
            ⚽ Goalkeeper count: {gkCount} {gkCount === 0 ? '(need at least 1!)' : gkCount > 2 ? '(max 2!)' : '(✓ OK)'}
          </div>
        )}

        {/* Floating confirmation button */}
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 to-transparent flex justify-center z-20">
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={confirmTeam}
            disabled={!selectionComplete}
            className={`px-6 sm:px-10 py-3 rounded-full font-bold text-base sm:text-xl shadow-2xl transition-all duration-200 ${
              selectionComplete
                ? 'bg-yellow-500 hover:bg-yellow-600 text-black cursor-pointer'
                : 'bg-gray-600 text-gray-300 cursor-not-allowed'
            }`}
          >
            {selectionComplete
              ? '✅ Confirm Team & Start Game'
              : '❌ Select one card from each tier (with at least one Goalkeeper)'}
          </motion.button>
        </div>
      </div>
    </div>
  );
};

export default TeamSelection;